import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getProfileCompletion } from '../../services/profileService';
import { isPrestataireLibreRole, isClientRole } from '../../utils/roleUtils';
import { useAcheteurProfile } from '../../contexts/AcheteurProfileContext';
import './PopupProfilIncomplet.css';

// Rappel differe d'1 heure quand l'utilisateur choisit "Me rappeler ulterieurement".
const SNOOZE_MS = 60 * 60 * 1000;

// ─── Popup d'incitation a completer le profil (acheteurs + prestataires libres) ──
// Non bloquante : carte d'angle (slide depuis la droite), l'utilisateur garde la main.
const PopupProfilIncomplet: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { completion: acheteurCompletion } = useAcheteurProfile?.() || { completion: null };
  const [completion, setCompletion] = useState<number | null>(null);
  const [visible, setVisible] = useState(false);

  const isAcheteur = !!user?.role && isClientRole(user.role);
  const isPrestaLibre = !!user?.role && isPrestataireLibreRole(user.role);
  const onProfil = location.pathname === '/profil';
  const snoozeKey = `centralis_profil_popup_snooze_${(user as any)?.id ?? (user as any)?.email ?? ''}`;

  // Recupere la completion selon le role.
  useEffect(() => {
    if (!user) return;
    if (isPrestaLibre) {
      getProfileCompletion()
        .then((data) => setCompletion(data.percent))
        .catch(() => { });
    } else if (isAcheteur && typeof acheteurCompletion?.percent === 'number') {
      setCompletion(acheteurCompletion.percent);
    }
  }, [user, isPrestaLibre, isAcheteur, acheteurCompletion]);

  // Affiche si incomplet et hors page profil ; programme la reapparition a
  // l'expiration du snooze (sans rechargement). Se ré-arme a chaque fermeture.
  useEffect(() => {
    if (visible || onProfil) return;
    if (completion === null || completion >= 100) return;
    const until = Number(localStorage.getItem(snoozeKey) || 0);
    const delay = Math.max(0, until - Date.now());
    const timer = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timer);
  }, [visible, onProfil, completion, snoozeKey]);

  // En arrivant sur la page profil, on ferme la popup si elle etait ouverte.
  useEffect(() => {
    if (onProfil) setVisible(false);
  }, [onProfil]);

  if (!visible || completion === null || onProfil) return null;

  const snooze = () => localStorage.setItem(snoozeKey, String(Date.now() + SNOOZE_MS));
  const snoozeAndClose = () => { snooze(); setVisible(false); };
  // "Completer" : on snooze aussi pour ne pas reapparaitre pendant qu'on remplit le profil.
  const goToProfil = () => { snooze(); setVisible(false); navigate('/profil'); };

  return (
    <div className="popup-profil-toast" role="dialog" aria-labelledby="popup-profil-title">
      <div className="popup-profil-modal">
        <button className="popup-profil-close" onClick={snoozeAndClose} aria-label="Fermer">
          ×
        </button>
        <h2 id="popup-profil-title" className="popup-profil-title">
          Complétez votre profil
        </h2>
        <p className="popup-profil-body">
          Votre profil n'est rempli qu'à <strong>{completion}%</strong>.{' '}
          Prochainement, il sera indispensable d’avoir un profil complet pour poursuivre l’utilisation de Centralis.
        </p>
        <div className="popup-profil-actions">
          <button className="popup-profil-cta" onClick={goToProfil}>
            Compléter mon profil
          </button>
          <button className="popup-profil-dismiss" onClick={snoozeAndClose}>
            Me rappeler ultérieurement
          </button>
        </div>
      </div>
    </div>
  );
};

export default PopupProfilIncomplet;
